import type { Merchant, OverviewMerchant, OverviewValues } from "@unbox/api";
import { useMemo, useRef, useState } from "react";
import { useOverview, useOverviewActiveCreators } from "@/api/overview";
import { useOverviewPinsPreference, useOverviewTotalsCurrencyPreference } from "@/api/user-preferences";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { RangeToggle } from "@/pages/home/components/range-toggle";
import {
  DEFAULT_RANGE_ID,
  type DistributionRange,
  latestSelectableDate,
  presetRange,
  type Selection,
} from "@/pages/home/lib/ranges";
import { useStuck } from "@/pages/home/lib/use-stuck";
import { BiggestMoversPanel, GmvByRegionPanel, type MoverRow } from "./components/insight-panels";
import { MerchantTable, type RowHighlight } from "./components/merchant-table";
import { type MetricTile, MetricTiles } from "./components/metric-tiles";
import { OverviewHero } from "./components/overview-hero";
import { RegionFilter } from "./components/region-filter";
import { TotalsCurrencyPicker } from "./components/totals-currency-picker";
import { conversionFactor, defaultTotalsCurrency, inCurrency, localCurrencyOf } from "./lib/currency";
import { buildMerchantRows, DEFAULT_SORT, nextSort, type SortKey, type SortState } from "./lib/merchant-rows";
import { DEFAULT_PINNED_METRICS, metricByKey, readMetric, sumValues, type Totals } from "./lib/metrics";
import { biggestMovers } from "./lib/movers";
import { comparisonDates, comparisonPhrase, periodTitle } from "./lib/period";
import { resolvePins } from "./lib/pins";
import { pickRegionFromPanel, regionKey, regionOptions, summariseRegions } from "./lib/regions";

const STUCK_SHADOW = "0 6px 16px -8px rgba(33,19,58,.14), 0 2px 6px -2px rgba(33,19,58,.06)";
const MIN_CUSTOM_DAYS = 2;

type WorkspaceOverviewPageProps = {
  merchants: Merchant[];
  workspaceId?: string;
};

const resolveRange = (selection: Selection, maxDate: string): DistributionRange =>
  selection.id === "custom" ? selection : presetRange(selection.id, new Date(), maxDate);

type PricedMerchant = OverviewMerchant & { factor: number };

const sumGmv = (merchants: PricedMerchant[], pick: (merchant: OverviewMerchant) => number) =>
  merchants.length ? merchants.reduce((sum, merchant) => sum + pick(merchant) * merchant.factor, 0) : null;

const withCreators = (totals: Totals, activeCreators: number | undefined): Totals =>
  activeCreators === undefined ? totals : { ...totals, activeCreators };

const merchantHomeUrl = (merchantId: string, workspaceId?: string) =>
  workspaceId ? `/workspace/${workspaceId}/merchant/${merchantId}/home` : `/merchant/${merchantId}/home`;

export function WorkspaceOverviewPage({ merchants, workspaceId }: WorkspaceOverviewPageProps) {
  const maxDate = useMemo(() => latestSelectableDate(new Date()), []);
  const [selection, setSelection] = useState<Selection>({ id: DEFAULT_RANGE_ID });
  const [regions, setRegions] = useState<string[]>([]);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [highlight, setHighlight] = useState<RowHighlight | null>(null);
  const pinsPreference = useOverviewPinsPreference();
  const pins = resolvePins(pinsPreference.saved);
  const currencyPreference = useOverviewTotalsCurrencyPreference();
  const sentinel = useRef<HTMLDivElement>(null);
  const stuck = useStuck(sentinel);

  const range = useMemo(() => resolveRange(selection, maxDate), [selection, maxDate]);
  const overview = useOverview(range, workspaceId);
  const data = currencyPreference.isLoading ? undefined : overview.data;
  const all = data?.merchants ?? [];
  const rates = data?.rates ?? null;

  const scoped = regions.length ? all.filter((merchant) => regions.includes(regionKey(merchant.region))) : all;
  const scopedIds = regions.length ? scoped.map((merchant) => merchant.merchantId) : null;
  const creators = useOverviewActiveCreators(range, scopedIds, workspaceId);

  const totalsCurrency = currencyPreference.saved ?? defaultTotalsCurrency(all, rates);
  const localCurrency = localCurrencyOf(regions, scoped);
  const currency = localCurrency ?? totalsCurrency;
  const factorOf = (from: string) => conversionFactor(from, currency, rates);
  const priced = (merchants: OverviewMerchant[]): PricedMerchant[] =>
    merchants.flatMap((merchant) => {
      const factor = factorOf(merchant.currency);
      return factor === null ? [] : [{ ...merchant, factor }];
    });
  const pricedAll = priced(all);
  const pricedScoped = priced(scoped);

  const identities = useMemo(
    () => new Map(merchants.map((merchant) => [merchant.id, { name: merchant.name, logoUrl: merchant.logoUrl }])),
    [merchants],
  );

  const options = regionOptions(all.map((merchant) => regionKey(merchant.region)));
  const singleRegion = options.length < 2;
  const comparison = comparisonPhrase(range);
  const comparisonTitle = overview.data ? comparisonDates(overview.data.priorPeriod) : comparison;

  const regionSummaries = summariseRegions(
    all.map((merchant) => ({
      regionKey: regionKey(merchant.region),
      currency: merchant.currency,
      currentGmv: merchant.current.shopGmv,
      priorGmv: merchant.prior.shopGmv,
      factor: factorOf(merchant.currency),
    })),
  );

  const movers = biggestMovers(
    scoped.map((merchant) => ({
      merchantId: merchant.merchantId,
      currency: merchant.currency,
      currentGmv: merchant.current.shopGmv,
      priorGmv: merchant.prior.shopGmv,
      factor: factorOf(merchant.currency),
    })),
  );
  const moverRow = (mover: (typeof movers.gaining)[number]): MoverRow => {
    const identity = identities.get(mover.merchantId);
    const merchant = all.find((entry) => entry.merchantId === mover.merchantId);
    return {
      ...mover,
      name: identity?.name ?? mover.merchantId,
      logoUrl: identity?.logoUrl ?? null,
      regionKey: regionKey(merchant?.region ?? null),
    };
  };

  const pricedTotalsOf = (pick: (merchant: OverviewMerchant) => OverviewValues) =>
    sumValues(pricedScoped.map((merchant) => inCurrency(pick(merchant), merchant.factor)));

  const current = withCreators(sumValues(scoped.map((merchant) => merchant.current)), creators.data?.current);
  const prior = withCreators(sumValues(scoped.map((merchant) => merchant.prior)), creators.data?.prior);
  const pricedCurrent = pricedTotalsOf((merchant) => merchant.current);
  const pricedPrior = pricedTotalsOf((merchant) => merchant.prior);
  const periodCount = all[0]?.series.length ?? 0;
  const seriesTotals = Array.from({ length: periodCount }, (_, index) => ({
    all: withCreators(
      sumValues(scoped.map((merchant) => merchant.series[index])),
      creators.data?.series[index]?.activeCreators,
    ),
    priced: pricedTotalsOf((merchant) => merchant.series[index]),
  }));
  const tiles: MetricTile[] = pins.map((key) => {
    const metric = metricByKey(key);
    return {
      metric,
      reading: metric.money ? readMetric(metric, pricedCurrent, pricedPrior) : readMetric(metric, current, prior),
      series: seriesTotals.map((totals) => metric.value(metric.money ? totals.priced : totals.all) ?? 0),
    };
  });

  const rows = buildMerchantRows(all, identities, factorOf).filter(
    (row) => regions.length === 0 || regions.includes(row.regionKey),
  );

  const pickMover = (merchantId: string) => {
    setSort(DEFAULT_SORT);
    setHighlight((previous) => ({ merchantId, nonce: (previous?.nonce ?? 0) + 1 }));
  };

  const changeSort = (key: SortKey) => {
    setHighlight(null);
    setSort((previous) => nextSort(previous, key));
  };

  const regionPanel = (
    <GmvByRegionPanel
      regions={regionSummaries}
      totalsCurrency={currency}
      selected={regions}
      comparison={comparison}
      onPick={(key) => setRegions((previous) => pickRegionFromPanel(previous, key))}
    />
  );
  const moversPanel = (
    <BiggestMoversPanel
      totalsCurrency={currency}
      gaining={movers.gaining.map(moverRow)}
      declining={movers.declining.map(moverRow)}
      comparison={comparison}
      scope={regions}
      onPick={pickMover}
    />
  );

  return (
    <div className="flex h-[calc(100vh-var(--sidebar-top-offset,0px))] w-full flex-col overflow-y-auto bg-unbox-cream-50">
      <OverviewHero
        range={range}
        total={sumGmv(pricedAll, (merchant) => merchant.current.shopGmv)}
        priorTotal={sumGmv(pricedAll, (merchant) => merchant.prior.shopGmv)}
        currency={currency}
      />

      <div className="flex w-full flex-col pt-10 pb-16">
        <div ref={sentinel} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
        <div
          style={{ boxShadow: stuck ? STUCK_SHADOW : "none", transition: "box-shadow 200ms ease" }}
          className="sticky top-0 z-30 w-full bg-unbox-cream-50"
        >
          <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center justify-between gap-4 px-20 py-3">
            <h2 className="text-[28px] leading-9 font-semibold tracking-[-0.01em] text-[#21133a]">
              Portfolio performance · {periodTitle(range)}
            </h2>
            <div className="flex items-center gap-3">
              <RegionFilter options={options} selected={regions} onChange={setRegions} />
              <RangeToggle range={range} maxDate={maxDate} minDays={MIN_CUSTOM_DAYS} onChange={setSelection} />
            </div>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-8 px-20 pt-6">
          {(overview.isLoading || currencyPreference.isLoading) && (
            <div className="flex justify-center py-24">
              <LoadingIndicator size="md" label="Loading..." />
            </div>
          )}
          {overview.isError && (
            <p className="py-24 text-center text-sm text-[#21133a]/70">
              We couldn't load your merchants' performance. Try again in a moment.
            </p>
          )}
          {data && all.length === 0 && (
            <p className="py-24 text-center text-sm text-[#21133a]/70">There are no merchants to show yet.</p>
          )}
          {data && all.length > 0 && (
            <>
              <div className="flex flex-col gap-5 min-[1100px]:flex-row">
                {singleRegion ? moversPanel : regionPanel}
                {singleRegion ? regionPanel : moversPanel}
              </div>
              {!pinsPreference.isLoading && (
                <MetricTiles
                  tiles={tiles}
                  comparisonTitle={comparisonTitle}
                  pins={pins}
                  onPinsChange={pinsPreference.save}
                  onReset={() => pinsPreference.save(DEFAULT_PINNED_METRICS)}
                  currency={currency}
                  currencyPicker={
                    <TotalsCurrencyPicker
                      selected={totalsCurrency}
                      local={localCurrency ? { currency: localCurrency, regions } : null}
                      onChange={currencyPreference.save}
                    />
                  }
                />
              )}
              <MerchantTable
                rows={rows}
                totalsCurrency={currency}
                sort={sort}
                onSort={changeSort}
                highlight={highlight}
                collapseKey={regions.join(",")}
                onOpenMerchant={(merchantId) =>
                  window.open(merchantHomeUrl(merchantId, workspaceId), "_blank", "noopener")
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
